<template>
  <div
    v-if="isHandoverApproved"
    class="bg-white rounded-lg p-3 text-left text-textPrimary"
    data-testid="handover-history-event"
  >
    <div
      class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold mb-3"
      style="background-color: #edf5ff; border-color: #0f62fe; color: #0f62fe"
      data-testid="handover-history-event-badge"
    >
      <svg class="w-4 h-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
        <path :d="mdiSwapHorizontal" fill="currentColor" />
      </svg>
      Troca de plantão
    </div>

    <div class="flex items-center gap-3 flex-wrap">
      <div class="flex items-center gap-2 min-w-0" data-testid="handover-history-event-from">
        <span
          class="flex items-center justify-center w-9 h-9 rounded-full bg-neutral-200 text-xs font-bold text-textPrimary shrink-0"
        >
          {{ handoverFrom.initials }}
        </span>
        <span class="min-w-0">
          <span class="block text-xs font-semibold text-[#7f7f8e]">Passou</span>
          <span class="block truncate font-medium">
            {{ handoverFrom.name
            }}<template v-if="handoverFrom.crm"> (CRM {{ handoverFrom.crm }})</template>
          </span>
        </span>
      </div>

      <svg class="w-5 h-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true" style="color: #0f62fe">
        <path :d="mdiArrowRightThin" fill="currentColor" />
      </svg>

      <div class="flex items-center gap-2 min-w-0" data-testid="handover-history-event-to">
        <span
          class="flex items-center justify-center w-9 h-9 rounded-full bg-neutral-200 text-xs font-bold text-textPrimary shrink-0"
        >
          {{ handoverTo.initials }}
        </span>
        <span class="min-w-0">
          <span class="block text-xs font-semibold text-[#7f7f8e]">Recebeu</span>
          <span class="block truncate font-medium">
            {{ handoverTo.name
            }}<template v-if="handoverTo.crm"> (CRM {{ handoverTo.crm }})</template>
          </span>
        </span>
      </div>
    </div>

    <hr class="my-3" />

    <p class="text-[#666]" data-testid="handover-history-event-footer">
      <template v-if="isAutomaticEffectuation">Efetivada automaticamente</template>
      <template v-else
        >Aprovado por <strong>{{ props.user?.name }}</strong></template
      >
    </p>
    <p class="text-[#666] text-sm">{{ formattedEditDate }}</p>
  </div>

  <div v-else class="bg-white rounded-lg p-3 text-left text-textPrimary">
    <div
      v-if="flags.enabledPermanenceRecord && isAutoPermanence"
      class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold mb-3"
      style="background-color: #eef6f7; border-color: #2f7f91; color: #2f7f91"
      data-testid="permanence-history-badge"
    >
      🔗 REGISTRO DE PERMANÊNCIA
    </div>
    <template v-if="coverageEvent">
      <span
        class="inline-flex rounded px-2 py-0.5 text-xs font-semibold mb-2 bg-[#E9E9F1] text-[#3E3E5A]"
        data-testid="coverage-history-badge"
        >{{ coverageEvent.badge }}</span
      >
      <p class="font-bold text-md mb-1" data-testid="coverage-history-title">
        {{ coverageEvent.title }}
      </p>
      <p class="text-sm text-[#666] mb-2">{{ props.user?.name }} · {{ formattedEditDate }}</p>
      <p v-if="coverageEvent.statusFrom" class="text-sm mb-2" data-testid="coverage-history-status">
        Status {{ coverageEvent.statusFrom }} → {{ coverageEvent.statusTo }}
      </p>
      <button
        type="button"
        class="text-sm font-bold text-primary30 underline"
        data-testid="coverage-history-toggle-diff"
        @click="showCoverageDiff = !showCoverageDiff"
      >
        {{ showCoverageDiff ? 'Ocultar campos alterados' : 'Ver campos alterados' }}
      </button>
    </template>
    <template v-else>
      <p class="font-bold text-md mb-2">
        {{ props.user?.name }} <span class="font-normal">{{ actionText }}</span>
      </p>
      <hr />
    </template>
    <div
      v-for="(change, index) in visibleChanges"
      :key="`${change.key}-${index}`"
      :data-testid="isReasonChange(change) ? 'history-change-reason-row' : undefined"
    >
      <p class="font-bold text-md mt-2 mb-2">{{ change.key }}</p>
      <div class="flex items-center gap-2 bg-neutral-200 py-1 px-2 rounded-md mb-2">
        <p v-if="change.from !== 'hide_field'" class="truncate min-w-0">
          {{ change.from }}
          <v-tooltip
            v-if="change.from !== 'hide_field'"
            activator="parent"
            location="top start"
            max-width="250"
            >{{ String(change.from) }}</v-tooltip
          >
        </p>
        <v-icon v-if="change.from !== 'hide_field'" color="text-textPrimary">{{
          mdiArrowRightThin
        }}</v-icon>
        <p class="truncate min-w-0">
          {{ change.to }}
          <v-tooltip
            v-if="change.to !== 'hide_field'"
            activator="parent"
            location="top end"
            max-width="250"
            >{{ String(change.to) }}</v-tooltip
          >
        </p>
      </div>
    </div>
    <p v-if="!coverageEvent" class="text-[#666]">{{ formattedEditDate }}</p>
    <p
      v-if="flags.enabledPermanenceRecord && isAutoPermanence && permanenceNote"
      class="text-[#666] text-xs mt-1"
      data-testid="permanence-history-note"
    >
      {{ permanenceNote }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { mdiArrowRightThin, mdiSwapHorizontal } from '@mdi/js'
import { computed, ref } from 'vue'
import { EditHistoryChange, EditHistoryUser } from '@/Network/Types/Responses/EditHistoryResponses'
import { timezones } from '@/hooks/useCommon'
import { humanizeDSReminderStatus } from '@/utils/doctorStation'
import { formatHourMinuteInOffset } from '@/utils/date'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { describeCoverageHistoryEvent } from '@/utils/coverageHistory'
import { UNIDENTIFIED_COVERAGE_LABEL } from '@/utils/coverage'

const COVERAGE_STATUS_HISTORY_LABELS: Record<string, string> = {
  OPEN: 'Vaga aberta',
  DOCTOR: 'Com médico',
  UNIDENTIFIED: UNIDENTIFIED_COVERAGE_LABEL
}

const COVERAGE_RESPONSIBLE_LABEL = 'Responsável pela cobertura'
const CHANGE_REASON_NOTE_LABEL = 'Descrição do motivo'

const props = defineProps<{
  user: EditHistoryUser
  createdAt: string
  changes: EditHistoryChange[]
  timezone: string | undefined
  type?: string
  isAutoPermanence?: boolean
  permanenceNote?: string | null
}>()

const { flags } = useFeatureFlags()

// M12-A — evento semântico de cobertura (ocupado sem identificação): o card
// mostra a narrativa + linha de status e deixa o diff bruto recolhido.
const coverageEvent = computed(() => describeCoverageHistoryEvent(props.changes, props.type))
const showCoverageDiff = ref(false)

// Evento estruturado de troca de plantão (contrato C2 do audits): backend
// devolve type HANDOVER_APPROVED com um change key="doctor" no formato
// "Nome (CRM xxxx)" — parseado defensivamente pois o CRM é opcional/variável.
const isHandoverApproved = computed(() => props.type === 'HANDOVER_APPROVED')

interface ParsedHandoverDoctor {
  name: string
  crm: string
  initials: string
}

const buildInitials = (name: string): string => {
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

const parseHandoverDoctor = (value: string | boolean | null | undefined): ParsedHandoverDoctor => {
  const raw = typeof value === 'string' ? value.trim() : ''
  const match = raw.match(/^(.*?)\s*\(\s*CRM[:.\s]*([^)]*)\)\s*$/i)
  const name = (match ? match[1] : raw).trim()
  const crm = (match ? match[2] : '').trim()
  return { name, crm, initials: buildInitials(name) }
}

const handoverDoctorChange = computed(() =>
  props.changes?.find((change) => change.key === 'doctor')
)

const handoverFrom = computed(() => parseHandoverDoctor(handoverDoctorChange.value?.from))
const handoverTo = computed(() => parseHandoverDoctor(handoverDoctorChange.value?.to))

// user = quem efetivou a troca (contrato C2). Quando é o próprio médico B
// (efetivação automática sem admin), o rodapé troca "Aprovado por" pelo aviso.
const isAutomaticEffectuation = computed(() => {
  const doctorName = handoverTo.value.name.trim().toLowerCase()
  const approverName = props.user?.name?.trim().toLowerCase()
  if (!doctorName || !approverName) return false
  return doctorName === approverName
})

const actionText = computed(() => {
  const map: Record<string, string> = {
    STATION: 'editou esse plantão',
    DOCTOR_STATION: 'editou esse plantão',
    DOCTOR_STATION_CREATE: 'criou esse plantão',
    CHECK: 'editou o check desse plantão',
    CHECK_CREATE: 'fez check-in nesse plantão',
    OBSERVATION_CREATE: 'adicionou uma observação',
    OBSERVATION_DELETE: 'apagou uma observação'
  }
  return map[props.type || ''] || 'editou esse plantão'
})

const formattedEditDate = computed(() => {
  if (!props.createdAt) return ''
  return `Em ${formatDate(props.createdAt)} às ${formatTime(props.createdAt, props.timezone)} (${timezoneTitle(props.timezone)})`
})

const timezoneTitle = (timezone: any) => {
  const found = timezones.find((tz) => tz.value === timezone)
  return found?.title
}

const formatDate = (value: string) => {
  const date = new Date(value)
  return date.toLocaleDateString('pt-BR')
}

const formatTime = (value: string, timezone: string | undefined) => {
  const date = new Date(value)
  // Fuso em formato de offset ISO (ex.: '-03:00', o valor padrão persistido
  // pelo backend — ver `timezones` em `src/hooks/useCommon.ts`) faz a opção
  // `timeZone` do `Intl` lançar `RangeError: Invalid time zone specified`,
  // que só aceita nome IANA. Esse é o caminho comum, então detectamos o
  // formato de offset ANTES de chamar o Intl e usamos o fallback direto,
  // sem passar pelo try/catch (evita warn no console a cada render).
  if (timezone && /^[+-]\d{2}:\d{2}$/.test(timezone)) {
    return formatHourMinuteInOffset(value, timezone) ?? ''
  }
  try {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone
    })
  } catch (error) {
    // Caminho excepcional: fuso não é offset nem nome IANA reconhecido pelo
    // Intl. Mesmo fallback já usado em `src/utils/date.ts` para o
    // pós-plantão: aritmética de offset em vez do `Intl`.
    console.warn('HistoryCard: fallback de formatTime para fuso não-IANA', timezone, error)
    return formatHourMinuteInOffset(value, timezone ?? '') ?? ''
  }
}

// SPEC §6: o backend manda a chave crua da coluna e o rótulo do motivo em `to` (from = hide_field).
const CHANGE_REASON_HISTORY_LABELS: Record<string, string> = {
  opening_reason: 'Motivo da abertura',
  external_reason: 'Motivo da inclusão de médico externo',
  removal_reason: 'Motivo da remoção',
  deletion_reason: 'Motivo da exclusão',
  opening_reason_note: CHANGE_REASON_NOTE_LABEL,
  external_reason_note: CHANGE_REASON_NOTE_LABEL,
  removal_reason_note: CHANGE_REASON_NOTE_LABEL,
  deletion_reason_note: CHANGE_REASON_NOTE_LABEL
}
const CHANGE_REASON_ROW_LABELS = new Set(Object.values(CHANGE_REASON_HISTORY_LABELS))
const isReasonChange = (change: { key: string }) => CHANGE_REASON_ROW_LABELS.has(change.key)

const formattedChanges = computed(() => {
  const fieldMap = {
    doctor_id: 'Médico',
    doctor: 'Médico',
    our_team: 'Time Interno',
    ourTeam: 'Time Interno',
    date: 'Data',
    value: 'Valor',
    inCash: 'Modalidade de repasse',
    timezone: 'Fuso Horário',
    request_antecipe: 'Adiantamento solicitado',
    requestAntecipe: 'Adiantamento solicitado',
    antecipe_was_paid: 'Adiantamento pago',
    antecipeWasPaid: 'Adiantamento pago',
    user_sponsor_id: 'Escalado por',
    userSponsorId: 'Escalado por',
    userSponsor: 'Escalado por',
    start_hour: 'Data e hora de início',
    startHour: 'Data e hora de início',
    end_hour: 'Data e hora de fim',
    endHour: 'Data e hora de fim',
    type: 'Periodicidade',
    isActive: 'Status da vaga',
    paymentRule: 'Regra de proporcionalidade',
    company: 'Empresa',
    modifiedValue: 'Modificou o valor',
    doctor_confirmed: 'Confirmou esse plantão',
    doctorConfirmed: 'Confirmou esse plantão',
    lastUpdaterUserId: 'Editou esse plantão',
    updatedAt: 'Em',
    paymentValue: 'Valor',
    ds_payment_doctor_formation_id: 'Formação',
    dsPaymentDoctorFormationId: 'Formação',
    dsPaymentDoctorFormation: 'Formação',
    insert_doctor_date: 'Data de inserção do médico',
    insertDoctorDate: 'Data de inserção do médico',
    position_id: 'Posição',
    positionId: 'Posição',
    position: 'Posição',
    doctor_station_observation_created: 'Observação criada',
    doctorStationObservationCreated: 'Observação criada',
    doctor_station_observation_deleted: 'Observação apagada',
    doctorStationObservationDeleted: 'Observação apagada',
    doctor_station_reminder_status: 'Status do lembrete de plantão',
    doctorStationReminderStatus: 'Status do lembrete de plantão',
    doctor_station_reminder_accepted_date: 'Data aceite lembrete de plantão',
    doctorStationReminderAcceptedDate: 'Data aceite lembrete de plantão',
    checkin: 'Check-in',
    checkin_latitude: 'Check-in latitude',
    checkin_longitude: 'Check-in longitude',
    checkout: 'Check-out',
    checkout_latitude: 'Check-out latitude',
    checkout_longitude: 'Check-out longitude',
    in_cash: 'Modalidade de repasse',
    doctor_station_reminder_rejected_date: 'Data rejeição lembrete de plantão',
    doctorStationReminderRejectedDate: 'Data rejeição lembrete de plantão',
    unregistered_doctor: 'Sem identificação',
    unregisteredDoctor: 'Sem identificação',
    coverage_responsible: COVERAGE_RESPONSIBLE_LABEL,
    coverageResponsible: COVERAGE_RESPONSIBLE_LABEL,
    coverage_responsible_id: COVERAGE_RESPONSIBLE_LABEL,
    coverageResponsibleId: COVERAGE_RESPONSIBLE_LABEL,
    coverage_responsible_name: COVERAGE_RESPONSIBLE_LABEL,
    coverageResponsibleName: COVERAGE_RESPONSIBLE_LABEL,
    coverage_note: 'Observação da cobertura',
    coverageNote: 'Observação da cobertura',
    // O BE manda a chave crua da coluna; o rótulo humano é montado aqui.
    coverage_status: 'Situação da cobertura',
    coverageStatus: 'Situação da cobertura',
    ...CHANGE_REASON_HISTORY_LABELS
  }

  const formatValue = (value: string | boolean | null): string => {
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não'
    if (value === null || value === '') return 'Vazio'
    return String(value)
  }

  // Valor desconhecido (enum novo no BE) passa cru em vez de sumir.
  const formatCoverageStatus = (value: string | boolean | null): string =>
    COVERAGE_STATUS_HISTORY_LABELS[String(value)] ?? formatValue(value)

  function formatChanges(): EditHistoryChange[] {
    const removeFields = [
      'date',
      'last_updater_user_id',
      'lastUpdaterUserId',
      'updatedAt',
      'updated_at',
      'isActive',
      'is_active',
      'createdAt',
      'created_at',
      'id',
      'deleted_at',
      'deletedAt',
      'doctor_station',
      'doctor_station_id',
      'doctorStationId',
      'is_main',
      'origin'
    ]
    const filteredChanges = props.changes?.filter((change) => !removeFields.includes(change.key))

    // Reordenação dos campos
    const startKeys = ['start_hour', 'startHour']
    const endKeys = ['end_hour', 'endHour']
    const startObject = filteredChanges.find((obj) => startKeys.includes(obj.key))
    const endObject = filteredChanges.find((obj) => endKeys.includes(obj.key))
    if (startObject && endObject) {
      const othersObjects = filteredChanges.filter(
        (obj) => !startKeys.includes(obj.key) && !endKeys.includes(obj.key)
      )
      return [startObject, endObject, ...othersObjects]
    }
    return filteredChanges
  }

  const formattedChanges = formatChanges()
  // eslint-disable-next-line complexity
  return formattedChanges?.map((change) => {
    const dateValues = [
      'endHour',
      'startHour',
      'start_hour',
      'end_hour',
      'insert_doctor_date',
      'insertDoctorDate',
      'date',
      'doctor_station_reminder_accepted_date',
      'doctorStationReminderAcceptedDate',
      'created_at',
      'updated_at',
      'createdAt',
      'updatedAt',
      'checkout',
      'checkin'
    ]
    if (dateValues.includes(change.key)) {
      return {
        key: fieldMap[change.key] || change.key,
        from:
          change.from === 'Vazio'
            ? change.from
            : `${formatDate(String(change.from))} ${formatTime(String(change.from), props.timezone)}`,
        to:
          change.to === 'Vazio'
            ? change.to
            : `${formatDate(String(change.to))} ${formatTime(String(change.to), props.timezone)}`
      }
    }
    if (change.key === 'timezone') {
      return {
        key: fieldMap[change.key] || change.key,
        from: change.from === 'Vazio' ? change.from : timezoneTitle(change.from),
        to: change.to === 'Vazio' ? change.to : timezoneTitle(change.to)
      }
    }
    if (
      change.key === 'doctor_station_reminder_status' ||
      change.key === 'doctorStationReminderStatus'
    ) {
      return {
        key: fieldMap[change.key] || change.key,
        from: humanizeDSReminderStatus(change.from),
        to: humanizeDSReminderStatus(change.to)
      }
    }
    if (change.key === 'coverage_status' || change.key === 'coverageStatus') {
      return {
        key: fieldMap[change.key],
        from: formatCoverageStatus(change.from),
        to: formatCoverageStatus(change.to)
      }
    }
    return {
      key: fieldMap[change.key] || change.key,
      from: formatValue(change.from),
      to: formatValue(change.to)
    }
  })
})

// Evento de cobertura: o diff fica atrás de "Ver campos alterados", mas o motivo fica sempre à vista.
const visibleChanges = computed(() =>
  coverageEvent.value && !showCoverageDiff.value
    ? formattedChanges.value.filter(isReasonChange)
    : formattedChanges.value
)
</script>
